import pRetry, { FailedAttemptError } from 'p-retry';
import {
  DATA_EXTRACTION_ROUTE,
  tokenizeRoute,
  CREATE_STUDY_NOTE_ROUTE,
  UPDATE_STUDY_NOTE_ROUTE,
} from '../../routes';
import { NonRetriableError } from 'query/errors';
import {
  sortTimepoints,
  sortOutcomesByTemplateField,
} from 'pages/DataExtraction/DataExtraction/dataUtils';
import {
  Arm,
  DataExtraction,
  Note,
  Outcome,
  ResultValue,
  OutcomeDetailValue,
} from 'types/DataExtraction';

export type RequestErrorHandler = (error: Error) => Error;

export { NonRetriableError };

export class OfflineError extends Error {
  statusCode: string;

  constructor(statusCode: string, message?: string) {
    super(message);
    this.name = 'OfflineError';
    this.statusCode = statusCode;
  }
}

export type DataExtractionError = NonRetriableError | OfflineError | Error;

export const DEFAULT_REQUEST_TIMEOUT = 60_000;

export const defaultFetchOptions = () => ({
  headers: { 'Content-Type': 'application/json' },
  signal: (AbortSignal as any).timeout(DEFAULT_REQUEST_TIMEOUT),
});
export const _handleJsonResponse = (response: Response) => {
  if (!response.ok) throw new NonRetriableError(String(response.status));

  return response.json();
};

export const withRequestRetry = (
  func: () => any,
  onError?: (error: Error) => void
) => {
  return pRetry(func, {
    // Default is exponential backoff, with 6 retries starting from 1 second => 32 seconds max wait time on the final attempt
    retries: 6,
    minTimeout: 1000, // Milliseconds before starting the first retry
    onFailedAttempt: (error: FailedAttemptError) => {
      if (error instanceof NonRetriableError) throw error;
      if (onError) onError(error);
    },
  });
};

export const getDataExtraction = async (
  reviewStudyId: string,
  onError?: RequestErrorHandler
): Promise<DataExtraction> => {
  return withRequestRetry(
    () => _getDataExtractionRequest(reviewStudyId),
    onError
  );
};

const _getDataExtractionRequest = async (
  reviewStudyId: string
): Promise<DataExtraction> => {
  if (!navigator.onLine)
    throw new OfflineError('No network connection detected');

  const url = tokenizeRoute(DATA_EXTRACTION_ROUTE, {
    review_study_id: reviewStudyId,
  });

  return fetch(url.toString(), {
    method: 'GET',
    ...defaultFetchOptions(),
  })
    .then((response) => {
      return _handleJsonResponse(response);
    })
    .then((response) => sortOutcomes(response))
    .then((response) => sortOutcomeTimepoints(response))
    .then((response) => populateExtractionResults(response))
    .then((response) => populateOutcomeResultValues(response))
    .then((response) => populateOutcomeDetailValues(response))
    .then((response) => {
      // populate arm position with index if it's not set
      return {
        ...response,
        arms: response.arms.map((arm: Arm, index: number) => {
          return { ...arm, position: arm.position ?? index };
        }),
      };
    });
};

export type CheckExistingExtractionDataResponse = {
  extracted: boolean;
};

const sortOutcomes = (dataExtraction: DataExtraction) => {
  dataExtraction.outcomes = dataExtraction.outcomes.sort(
    sortOutcomesByTemplateField()
  );
  return dataExtraction;
};

const sortOutcomeTimepoints = (dataExtraction: DataExtraction) => {
  dataExtraction.outcomes.forEach((outcome) => {
    outcome.timepoints = outcome.timepoints.sort(sortTimepoints(outcome));
  });
  return dataExtraction;
};

const resultValueForCell = (
  outcome: Outcome,
  outcome_result_id: string,
  arm_id: string,
  timepoint_id: string
) => {
  // Cast to any as at this point the results are the raw array received from the server, pre-transformation
  const resultValues = outcome.result_values as any;
  return resultValues.find(
    (resultValue: ResultValue) =>
      resultValue.outcome_result_id === outcome_result_id &&
      resultValue.arm_id === arm_id &&
      resultValue.timepoint_id === timepoint_id
  );
};

// Create a result object for each cell so the results are easily accessible via arm/timepoint/outcomeResult
export const populateOutcomeResultValues = (dataExtraction: DataExtraction) => {
  return {
    ...dataExtraction,
    outcomes: dataExtraction.outcomes.map((outcome) => {
      const resultValuesByCell: Outcome['result_values'] = {};

      dataExtraction.arms.forEach((arm) => {
        outcome.timepoints.forEach((timepoint) => {
          outcome.outcome_results.forEach((or) => {
            const resultValue = resultValueForCell(
              outcome,
              or.id,
              arm.id,
              timepoint.id
            ) ?? { value: '' };
            resultValuesByCell[or.id] ??= {};
            resultValuesByCell[or.id][arm.id] ??= {};
            resultValuesByCell[or.id][arm.id][timepoint.id] = resultValue;
          });
        });
      });

      outcome.result_values = resultValuesByCell;
      return outcome;
    }),
  };
};

/**
 * Transforms the `outcome_detail_values` of each outcome in the provided `DataExtraction` object
 * into a structured object where the details are accessible by their `template_field_id`.
 *
 * This function ensures that each `template_field_id` in `template_outcome_details` has a corresponding
 * entry in the `outcome_detail_values` object. If no matching detail exists, a default object with
 * `value: null` and the `template_field_id` is created.
 *
 * @param {DataExtraction} dataExtraction - The data extraction object containing outcomes and template details.
 * @returns {DataExtraction} A new `DataExtraction` object with updated `outcome_detail_values` for each outcome.
 *
 * ### Example:
 * Input:
 * ```typescript
 * const dataExtraction = {
 *   outcomes: [
 *     {
 *       outcome_detail_values: [
 *         { template_field_id: '1', value: 'A' },
 *         { template_field_id: '2', value: 'B' },
 *       ],
 *     },
 *   ],
 *   template_outcome_details: [
 *     { id: '1' },
 *     { id: '2' },
 *     { id: '3' },
 *   ],
 * };
 * ```
 *
 * Output:
 * ```typescript
 * {
 *   outcomes: [
 *     {
 *       outcome_detail_values: {
 *         '1': { template_field_id: '1', value: 'A' },
 *         '2': { template_field_id: '2', value: 'B' },
 *         '3': { template_field_id: '3', value: null },
 *       },
 *     },
 *   ],
 * }
 * ```
 *
 * ### Notes:
 * - This function does not modify the original `dataExtraction` object; it returns a new object.
 * - The `outcome_detail_values` property of each outcome is converted from an array to an object
 *   keyed by `template_field_id` for easier access.
 */
export const populateOutcomeDetailValues = (dataExtraction: DataExtraction) => {
  return {
    ...dataExtraction,
    outcomes: dataExtraction.outcomes.map((outcome) => {
      const detailsValuesByCell: Outcome['outcome_detail_values'] = {};

      dataExtraction.template_outcome_details.forEach(
        (templateOutcomeDetail) => {
          // Runtime assertion: template outcome details should always have an id
          // This is required to keep TypeScript compiler happy, but should never
          // occur in production.
          if (!templateOutcomeDetail.id) {
            throw new Error(
              `Template outcome detail is missing required id: ${JSON.stringify(
                templateOutcomeDetail
              )}`
            );
          }

          const outcomeDetail = (outcome.outcome_detail_values as any).find(
            (odv: OutcomeDetailValue) =>
              odv.template_field_id === templateOutcomeDetail.id
          );

          detailsValuesByCell[templateOutcomeDetail.id] = outcomeDetail || {
            value: null,
            template_field_id: templateOutcomeDetail.id,
          };
        }
      );

      outcome.outcome_detail_values = detailsValuesByCell;
      return outcome;
    }),
  };
};

// Creates a result object for each arm/characteristic combo
export const populateExtractionResults = (dataExtraction: DataExtraction) => {
  return {
    ...dataExtraction,
    intervention_characteristics: dataExtraction.intervention_characteristics.map(
      (characteristic) => {
        const existingArms = characteristic.results.reduce((prev, result) => {
          return { ...prev, [result.arm_id]: result };
        }, {}) as Record<string, { arm_id: string; value: string }>;

        return {
          ...characteristic,
          results: dataExtraction.arms.map((arm) => {
            if (existingArms[arm.id]) return existingArms[arm.id];
            return {
              arm_id: arm.id,
              value: '',
            };
          }),
        };
      }
    ),
    baseline_characteristics: (
      dataExtraction.baseline_characteristics ?? []
    ).map((characteristic) => {
      const existingResults = (characteristic.results ?? []).reduce(
        (prev, result) => {
          // Use the empty string as the key for the arm-less "Overall" result
          return { ...prev, [result.arm_id ?? '']: result };
        },
        {}
      ) as Record<string, { arm_id: string | null; value: string }>;

      // One result column per arm, plus a trailing "Overall" column (arm_id null)
      const armResults = dataExtraction.arms.map((arm) => {
        return existingResults[arm.id] ?? { arm_id: arm.id, value: '' };
      });
      const overallResult = existingResults[''] ?? { arm_id: null, value: '' };

      return {
        ...characteristic,
        results: [...armResults, overallResult],
      };
    }),
  };
};

export const createStudyNote = (
  note: string,
  category: string,
  reviewStudyId: string,
  onRequestError?: RequestErrorHandler
): Promise<Note> => {
  return withRequestRetry(
    () => _createStudyNote(note, category, reviewStudyId),
    onRequestError
  );
};

const _createStudyNote = async (
  note: string,
  category: string,
  reviewStudyId: string
): Promise<Note> => {
  if (!navigator.onLine)
    throw new OfflineError('No network connection detected');

  const url = tokenizeRoute(CREATE_STUDY_NOTE_ROUTE, {
    review_study_id: reviewStudyId,
  });

  const body: any = {
    note: note,
    category: category,
  };

  return fetch(url.toString(), {
    method: 'POST',
    body: JSON.stringify(body),
    ...defaultFetchOptions(),
  }).then((response) => {
    return _handleJsonResponse(response);
  });
};

export const updateStudyNote = (
  note: string,
  noteId: number,
  reviewStudyId: string,
  onRequestError?: RequestErrorHandler
): Promise<Note> => {
  return withRequestRetry(
    () => _updateStudyNote(note, noteId, reviewStudyId),
    onRequestError
  );
};

const _updateStudyNote = async (
  note: string,
  noteId: number,
  reviewStudyId: string
): Promise<Note> => {
  if (!navigator.onLine)
    throw new OfflineError('No network connection detected');

  const url = tokenizeRoute(UPDATE_STUDY_NOTE_ROUTE, {
    review_study_id: reviewStudyId,
    note_id: noteId,
  });

  const body: any = { note: note };

  return fetch(url.toString(), {
    method: 'PUT',
    body: JSON.stringify(body),
    ...defaultFetchOptions(),
  }).then((response) => {
    return _handleJsonResponse(response);
  });
};
